/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as API from "../../framework/API/api";
import { TOAST_TYPES } from "../../framework/constant/constant";
import ToastNotification from "../../components/ToastNotification";
import BossDialog from "../../components/BossDialog";
import MyMultiSelect from "../../components/MyMultiSelect";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-data-query";
import moment from "moment";
import { useTranslation } from "../../locale/useTranslation";
import { DateOnlyCell } from "../../framework/forms/helpercomponents/CustomGridCells/DateCell";
import { TimeCell } from "../../framework/forms/helpercomponents/CustomGridCells/TimeCell";
import ActionButton from "../../framework/forms/helpercomponents/buttons/ActionButton";
import { Loader } from "../../framework/forms/helpercomponents/Loader";

const DATA_ITEM_KEY = "_id";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

const getWeekRange = (baseDate) => {
    if (!baseDate) {
        const today = moment();
        return {
            fromDate: today.clone().startOf("isoWeek").format("YYYY-MM-DD"),
            toDate: today.clone().endOf("isoWeek").format("YYYY-MM-DD")
        };
    }
    const m = moment(baseDate);
    if (!m.isValid()) {
        const today = moment();
        return {
            fromDate: today.clone().startOf("isoWeek").format("YYYY-MM-DD"),
            toDate: today.clone().endOf("isoWeek").format("YYYY-MM-DD")
        };
    }
    return {
        fromDate: m.clone().startOf("isoWeek").format("YYYY-MM-DD"),
        toDate: m.clone().endOf("isoWeek").format("YYYY-MM-DD")
    };
};

const SearchPlanningContentPopup = (props) => {
    const { onClose, onNavigateToSlot, selectedChannel, filterChannels, date } = props;
    const lang = useTranslation();

    const style = {
        labelStyel: { fontSize: '11px', fontWeight: 600, color: '#3A3633', marginBottom: '3px', display: 'block' },
        sectionCard: { marginBottom: '16px', padding: '12px 14px', background: '#f5f5f5', borderRadius: '8px', border: '1px solid #e2e2e2' },
        posterCard: { padding: '10px 5px', background: '#f5f5f5', borderRadius: '8px', border: '1px solid #e2e2e2' },
        sectionHeader: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#3A3633', marginBottom: '10px', paddingLeft: '8px', borderLeft: '3px solid #3A3633' },
    };

    // Automatically pickup planning grid week range
    const initialRange = useMemo(() => getWeekRange(date), [date]);

    const [channels, setChannels] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [fromDate, setFromDate] = useState(initialRange.fromDate);
    const [toDate, setToDate] = useState(initialRange.toDate);
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedState, setSelectedState] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);

    // Grid server-side pagination
    const [skip, setSkip] = useState(0);
    const [take, setTake] = useState(50);
    const [total, setTotal] = useState(0);

    // Sync date range if date prop changes
    useEffect(() => {
        if (date) {
            const range = getWeekRange(date);
            setFromDate(range.fromDate);
            setToDate(range.toDate);
        }
    }, [date]);

    useEffect(() => {
        loadChannels();
    }, []);

    const loadChannels = async () => {
        try {
            const channelRes = await API.getValidChannels();
            const channelList = channelRes?.data || [];
            setChannels(channelList);

            if (filterChannels && filterChannels.length > 0) {
                const sids = filterChannels.map(c => c.SID || c._id);
                const matchedList = channelList.filter(c => sids.includes(c.SID) || sids.includes(c._id));
                if (matchedList.length > 0) {
                    setSelectedChannels(matchedList);
                    return;
                }
            }

            if (selectedChannel && (selectedChannel.SID || selectedChannel._id)) {
                const targetId = selectedChannel.SID || selectedChannel._id;
                const matched = channelList.find(c => c.SID === targetId || c._id === targetId);
                if (matched) {
                    setSelectedChannels([matched]);
                }
            }
        } catch (error) {
            console.error("Error loading channels", error);
        }
    };

    const isValid = () => {
        const queryText = (searchText || "")?.trim();
        if (!queryText || queryText == '') {
            ToastNotification({
                type: TOAST_TYPES.INFO,
                title: lang.info_message_title || "Info",
                message: "Please enter search text (Title, Asset ID)..."
            });
            return false;
        }
        return true;
    }

    const fetchPlanningData = async (currentPage = 1, currentTake = take) => {

        if (!isValid()) return;

        setLoading(true);
        setHasSearched(true);

        try {

            let fromTimestamp = null;
            let toTimestamp = null;
            if (fromDate) {
                fromTimestamp = new Date(fromDate + "T00:00:00").getTime();
            }
            if (toDate) {
                toTimestamp = new Date(toDate + "T23:59:59.999").getTime();
            }

            const channelSIDs = selectedChannels?.length > 0 ? selectedChannels.map(c => c.SID).filter(Boolean) : "";
            const payload = {
                searchText: (searchText || "")?.trim(),
                channelSID: channelSIDs,
                fromDate: fromTimestamp,
                toDate: toTimestamp,
                page: currentPage,
                perPage: currentTake
            };

            const res = await API.getPlanningByMedia(payload);
            setSearchResults(res?.data || []);
            setTotal(res?.pagination?.total || 0);
        } catch (error) {
            console.error("Error searching planning content:", error);
            ToastNotification({
                type: TOAST_TYPES.ERROR,
                title: lang.invalid_error_message_title,
                message: error.message || "Failed to search planning content"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setSelectedState({});
        setSelectedItem(null);
        setSkip(0);
        fetchPlanningData(1, take);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSearch();
        }
    };

    const handleReset = () => {
        setSearchText("");
        const range = getWeekRange(date);
        setFromDate(range.fromDate);
        setToDate(range.toDate);

        if (filterChannels && filterChannels.length > 0) {
            const sids = filterChannels.map(c => c.SID || c._id);
            const matchedList = channels.filter(c => sids.includes(c.SID) || sids.includes(c._id));
            setSelectedChannels(matchedList.length > 0 ? matchedList : []);
        } else if (selectedChannel) {
            const targetId = selectedChannel.SID || selectedChannel._id;
            const matched = channels.find(c => c.SID === targetId || c._id === targetId);
            setSelectedChannels(matched ? [matched] : []);
        } else {
            setSelectedChannels([]);
        }

        setSearchResults([]);
        setHasSearched(false);
        setSelectedState({});
        setSelectedItem(null);
        setSkip(0);
        setTotal(0);
    };

    const handleRowClick = (event) => {
        const item = event.dataItem;
        const id = idGetter(item);
        setSelectedState({ [id]: true });
        setSelectedItem(item);
    };

    const handleRowDoubleClick = (event) => {
        const item = event.dataItem;
        handleNavigate(item);
    };

    const onSelectionChange = useCallback((event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: {},
            dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
        const selected = event.dataItems.find(item => newSelectedState[idGetter(item)]);
        setSelectedItem(selected || null);
    }, []);

    const handleNavigate = (itemToNavigate) => {
        const item = itemToNavigate || selectedItem;
        if (!item) {
            ToastNotification({
                type: TOAST_TYPES.INFO,
                title: lang.info_message_title || "Info",
                message: "Please select a scheduled item to open in Planning"
            });
            return;
        }

        if (onNavigateToSlot) {
            onNavigateToSlot(item);
        }
        if (onClose) {
            onClose();
        }
    };

    const pageChange = (event) => {
        const newSkip = event.page.skip;
        const newTake = event.page.take;
        const newPage = Math.floor(newSkip / newTake) + 1;
        setSkip(newSkip);
        setTake(newTake);
        fetchPlanningData(newPage, newTake);
    };

    const customRowRender = (trElement, rowProps) => {
        const isSelected = selectedState[idGetter(rowProps.dataItem)];
        const rowStyle = {
            cursor: "pointer",
            backgroundColor: isSelected ? "#e0f2fe" : rowProps.style?.backgroundColor,
            fontWeight: isSelected ? "600" : "normal",
            transition: "background-color 0.15s ease",
            ...rowProps.style,
        };
        const trProps = {
            ...trElement.props,
            style: rowStyle,
            className: `${trElement.props.className || ""} ${isSelected ? "k-state-selected" : ""}`
        };
        return React.cloneElement(trElement, trProps, trElement.props.children);
    };

    return (
        <BossDialog
            onClose={onClose}
            title={lang.search_planned_content_label}
            width={"1260px"}
            height={"88vh"}
        >
            <div style={{ display: "flex", flexDirection: "column", height: "calc(88vh - 100px)", gap: "10px", overflow: "hidden" }}>
                {/* Filter Card */}
                <div style={{ ...style.sectionCard, marginBottom: "8px" }}>
                    <div className="row g-2 align-items-end">
                        {/* Search Title / Asset ID */}
                        <div className="col-12 col-md-3">
                            <label style={style.labelStyel}>
                                {lang.title_label || "Title"} / {lang.asset_id_column || "Asset ID"} *
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0 text-muted" style={{ borderColor: "#ced4da" }}>
                                    <i className="fa fa-search text-primary" style={{ fontSize: "12px" }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm border-start-0"
                                    style={{ height: "30px", fontSize: "13px" }}
                                    placeholder={"Search Title, Asset ID..."}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    autoFocus
                                />
                                {searchText && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setSearchText("")}
                                        title="Clear search text"
                                        style={{ height: "30px", padding: "0 8px" }}
                                    >
                                        <i className="fa fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Multi-Channel Selector */}
                        <div className="col-6 col-md-3">
                            <label style={style.labelStyel}>
                                {lang.channel_name_column || "Channels"}
                            </label>
                            <MyMultiSelect
                                data={channels}
                                name="Channels"
                                textField="FullChannelName"
                                dataItemKey="SID"
                                value={selectedChannels}
                                onChange={(e) => setSelectedChannels(e.value || e.target?.value || [])}
                                style={{ width: "100%", backgroundColor: "#fff" }}
                            />
                        </div>

                        {/* From Date */}
                        <div className="col-3 col-md-2">
                            <label style={style.labelStyel}>
                                {lang.from_date_label || "From Date"}
                            </label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                style={{ height: "30px", fontSize: "13px" }}
                                title="From Date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>

                        {/* To Date */}
                        <div className="col-3 col-md-2">
                            <label style={style.labelStyel}>
                                {lang.to_date_label || "To Date"}
                            </label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                style={{ height: "30px", fontSize: "13px" }}
                                title="To Date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>

                        {/* Action Buttons (Search & Reset) */}
                        <div className="col-12 col-md-2 d-flex align-items-end" style={{ gap: "6px" }}>
                            <ActionButton
                                title={lang.search_button_tooltip || "Search"}
                                name={lang.search_button_text || "Search"}
                                icon="search"
                                isLoading={loading}
                                onClick={handleSearch}
                                style={{ height: "30px", fontSize: "12px", flex: 1, whiteSpace: "nowrap" }}
                            />
                            <ActionButton
                                title={lang.reset_button_tooltip || "Reset"}
                                name={lang.reset_button_text || "Reset"}
                                icon="undo"
                                btnColor="secondary"
                                onClick={handleReset}
                                style={{ height: "30px", fontSize: "12px", flex: 1, whiteSpace: "nowrap" }}
                            />
                        </div>
                    </div>

                    {/* Filter Action Status Sub-Row */}
                    <div className="d-flex align-items-center mt-2 pt-2" style={{ borderTop: "1px solid #e2e2e2" }}>
                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            {hasSearched ? (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", backgroundColor: "#e8f5e9", border: "1px solid #c8e6c9", padding: "2px 8px", borderRadius: "12px", fontSize: "11.5px", color: "#2e7d32", fontWeight: "600" }}>
                                    <i className="fa fa-check-circle text-success"></i>
                                    <span><strong>{total}</strong> {total === 1 ? "record found" : "records found"}</span>
                                </span>
                            ) : (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", backgroundColor: "#e3f2fd", border: "1px solid #bbdefb", padding: "2px 8px", borderRadius: "12px", fontSize: "11.5px", color: "#1565c0", fontWeight: "500" }}>
                                    <i className="fa fa-calendar text-primary"></i>
                                    <span>Planning Week: <strong>{fromDate ? moment(fromDate).format("DD-MMM-YYYY") : "Start"}</strong> to <strong>{toDate ? moment(toDate).format("DD-MMM-YYYY") : "End"}</strong></span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Grid Results Section */}
                <div
                    style={{
                        flex: 1,
                        minHeight: "360px",
                        height: "100%",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative"
                    }}
                >
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center h-100 flex-column" style={{ minHeight: "300px" }}>
                            <Loader />
                            <div className="text-muted mt-2" style={{ fontSize: "13px", fontWeight: "500" }}>Searching planning schedules...</div>
                        </div>
                    ) : !hasSearched && searchResults.length === 0 ? (
                        <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted p-4" style={{ minHeight: "300px" }}>
                            <div style={{ width: "64px", height: "64px", borderRadius: "50%", backgroundColor: "#f0f9ff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "14px", border: "1px solid #e0f2fe" }}>
                                <i className="fa-solid fa-magnifying-glass" style={{ fontSize: "24px", color: "#0284c7" }}></i>
                            </div>
                            <h5 style={{ fontWeight: "700", color: "#1e293b", marginBottom: "6px" }}>{lang.search_planned_content_label}</h5>
                            <p style={{ fontSize: "13px", color: "#64748b", maxWidth: "460px", textAlign: "center", margin: 0, lineHeight: "1.5" }}>
                                Search programs scheduled across channels within the selected planning week range. Enter Title, Episode, or Asset ID and click <strong>Search</strong>.
                            </p>
                        </div>
                    ) : hasSearched && searchResults.length === 0 ? (
                        <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted p-4" style={{ minHeight: "300px" }}>
                            <div style={{ width: "64px", height: "64px", borderRadius: "50%", backgroundColor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "14px", border: "1px solid #fee2e2" }}>
                                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "24px", color: "#ef4444" }}></i>
                            </div>
                            <h5 style={{ fontWeight: "700", color: "#1e293b", marginBottom: "6px" }}>No Planning Records Found</h5>
                            <p style={{ fontSize: "13px", color: "#64748b", maxWidth: "460px", textAlign: "center", margin: 0, lineHeight: "1.5" }}>
                                No scheduled programs match your search criteria. Try modifying your search keywords, channel selection, or date range.
                            </p>
                        </div>
                    ) : (
                        <Grid
                            style={{ height: "100%", width: "100%", flex: 1, minHeight: "360px" }}
                            data={searchResults.map(item => ({
                                ...item,
                                [SELECTED_FIELD]: selectedState[idGetter(item)] || false
                            }))}
                            dataItemKey={DATA_ITEM_KEY}
                            selectedField={SELECTED_FIELD}
                            selectable={{
                                enabled: true,
                                mode: "single",
                                drag: false,
                                cell: false
                            }}
                            onSelectionChange={onSelectionChange}
                            onRowClick={handleRowClick}
                            onRowDoubleClick={handleRowDoubleClick}
                            rowRender={customRowRender}
                            pageable={{
                                buttonCount: 5,
                                info: true,
                                type: "numeric",
                                previousNext: true
                            }}
                            total={total}
                            skip={skip}
                            take={take}
                            onPageChange={pageChange}
                            resizable={true}
                        >
                            <GridColumn
                                field="ScheduleDate"
                                title={lang.date_label}
                                width="140px"
                                cell={DateOnlyCell}
                            />
                            <GridColumn
                                field="SlotDateTime"
                                title={lang.start_time_label}
                                width="115px"
                                cell={TimeCell}
                            />
                            <GridColumn
                                field="MediaEpisode.Duration"
                                title={lang.schedule_duration_column}
                                width="120px"
                                cell={TimeCell}
                            />
                            <GridColumn
                                field="MediaEpisode.AssetId"
                                title={lang.asset_id_column}
                                width="160px"
                            />
                            <GridColumn
                                field="MediaEpisode.Title"
                                title={lang.title_column}
                            />
                            <GridColumn
                                field="MediaEpisode.EpisodeNumber"
                                title={lang.episode_number_column}
                                width="110px"
                            />
                            <GridColumn
                                field="Channel.FullChannelName"
                                title={lang.channel_column}
                                width="170px"
                            />
                        </Grid>
                    )}
                </div>

                {/* Footer Selection Banner & Actions */}
                <div
                    className="d-flex justify-content-between align-items-center pt-2"
                    style={{
                        borderTop: "1px solid #e2e8f0",
                        marginTop: "2px"
                    }}
                >
                    <div style={{ fontSize: "12px", color: "#475569" }}>
                        {selectedItem ? (
                            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#10b981", display: "inline-block" }}></span>
                                <strong>{lang.selected_label}:</strong>
                                <span style={{ color: "#0284c7", fontWeight: "700" }}>
                                    {selectedItem?.MediaEpisode?.Title}
                                </span>
                                <span className="text-muted" style={{ fontWeight: "500" }}>
                                    ({moment(selectedItem.SlotDateTime).format("DD-MMM-YYYY")}
                                    {selectedItem.SlotDateTime ? ` at ${moment.utc(selectedItem.SlotDateTime).format("HH:mm:ss")}` : ""})
                                </span>
                            </div>
                        ) : (
                            <span className="text-muted" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <i className="fa-solid fa-lightbulb text-warning"></i>
                                <span>Double-click any row to navigate directly to that slot in Planning.</span>
                            </span>
                        )}
                    </div>
                    <div className="d-flex" style={{ gap: "8px" }}>
                        <ActionButton
                            title={lang.close_button_tooltip || "Close"}
                            name={lang.close_button_text || "Close"}
                            btnColor="secondary"
                            onClick={onClose}
                            style={{ height: "30px", fontSize: "12px" }}
                        />
                        <ActionButton
                            title={lang.go_to_planning_button_tooltip}
                            name={lang.go_to_planning_button}
                            icon="external-link"
                            disabled={!selectedItem}
                            onClick={() => handleNavigate()}
                            style={{ height: "30px", fontSize: "12px" }}
                        />
                    </div>
                </div>
            </div>
        </BossDialog>
    );
};

export default SearchPlanningContentPopup;
